/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
  getRohlingeAdmin,
  createRohling,
  updateRohling,
  deleteRohling,
  addFarbvariante,
  updateFarbvariante,
  deleteFarbvariante,
  getGravurfarben,
  getRohlingfarben,
  buildAssetUrl
} from '../services/api';
import './RohlingVerwaltung.css';

function RohlingVerwaltung() {
  const [rohlinge, setRohlinge] = useState([]);
  const [gravurfarben, setGravurfarben] = useState([]);
  const [rohlingfarben, setRohlingfarben] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRohlingId, setEditingRohlingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Schritt-für-Schritt-Workflow
  const showFarbvarianten = false;
  const [currentStep, setCurrentStep] = useState(1); // 1 = Grundeinstellungen, 2 = Farbvarianten

  // Grundeinstellungen (Schritt 1)
  const [grundeinstellungen, setGrundeinstellungen] = useState({
    name: '',
    breite: 30,
    hoehe: 40,
    beidseitig: false,
    frontSameAsBack: false,
    gravurfarbe: '',
    masterSvg: null,
    rohlingfarben: []
  });
  const [masterSvgFile, setMasterSvgFile] = useState(null);

  // Farbvarianten (Schritt 2)
  const [farbvarianten, setFarbvarianten] = useState([]);
  const [aktiveFarbvarianteIndex, setAktiveFarbvarianteIndex] = useState(0);

  // Canvas-Refs für Kalibrierung
  const frontCanvasRef = useRef(null);
  const backCanvasRef = useRef(null);

  // Kalibrierungs-Zustand
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const [drawingSide, setDrawingSide] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadRohlinge();
    loadGravurfarben();
    loadRohlingfarben();
  }, []);

  // Zeichne Canvas wenn sich Farbvariante ändert
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (farbvarianten.length > 0 && aktiveFarbvarianteIndex < farbvarianten.length) {
      const aktiv = farbvarianten[aktiveFarbvarianteIndex];
      if (aktiv.frontImagePreview) {
        drawImageWithOverlays('front');
      }
      if (aktiv.backImagePreview) {
        drawImageWithOverlays('back');
      }
    }
  }, [aktiveFarbvarianteIndex, farbvarianten]);

  // Zeichne Canvas wenn sich Kalibrierungsparameter ändern
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (farbvarianten.length > 0 && aktiveFarbvarianteIndex < farbvarianten.length) {
      const aktiv = farbvarianten[aktiveFarbvarianteIndex];
      if (aktiv.frontImagePreview) {
        drawImageWithOverlays('front');
      }
    }
  }, [
    farbvarianten[aktiveFarbvarianteIndex]?.frontScale,
    farbvarianten[aktiveFarbvarianteIndex]?.frontImageOffset,
    farbvarianten[aktiveFarbvarianteIndex]?.frontRotation,
    farbvarianten[aktiveFarbvarianteIndex]?.isCalibrating?.front
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (farbvarianten.length > 0 && aktiveFarbvarianteIndex < farbvarianten.length) {
      const aktiv = farbvarianten[aktiveFarbvarianteIndex];
      if (aktiv.backImagePreview) {
        drawImageWithOverlays('back');
      }
    }
  }, [
    farbvarianten[aktiveFarbvarianteIndex]?.backScale,
    farbvarianten[aktiveFarbvarianteIndex]?.backImageOffset,
    farbvarianten[aktiveFarbvarianteIndex]?.backRotation,
    farbvarianten[aktiveFarbvarianteIndex]?.isCalibrating?.back
  ]);

  const loadRohlinge = async () => {
    try {
      setLoading(true);
      const data = await getRohlingeAdmin();
      setRohlinge(data);
    } catch (error) {
      showMessage('error', 'Fehler beim Laden der Rohlinge');
    } finally {
      setLoading(false);
    }
  };

  const loadGravurfarben = async () => {
    try {
      const data = await getGravurfarben();
      const list = Array.isArray(data) ? data : [];
      setGravurfarben(list);
      // Setze erste Farbe als Default wenn noch keine ausgewählt
      if (list.length > 0 && !grundeinstellungen.gravurfarbe) {
        setGrundeinstellungen(prev => ({ ...prev, gravurfarbe: list[0].hexColor }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Gravurfarben:', error);
      setGravurfarben([]);
    }
  };

  const loadRohlingfarben = async () => {
    try {
      const data = await getRohlingfarben();
      setRohlingfarben(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fehler beim Laden der Rohling-Farben:', error);
      setRohlingfarben([]);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const resetForm = () => {
    setGrundeinstellungen({
      name: '',
      breite: 30,
      hoehe: 40,
      beidseitig: false,
      frontSameAsBack: false,
      gravurfarbe: gravurfarben[0]?.hexColor || '',
      masterSvg: null,
      rohlingfarben: []
    });
    setMasterSvgFile(null);
    setFarbvarianten([]);
    setAktiveFarbvarianteIndex(0);
    setCurrentStep(1);
    setEditingRohlingId(null);
    setShowForm(false);
  };

  const handleGrundeinstellungenChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGrundeinstellungen(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
    }));
  };

  const handleRohlingfarbeToggle = (farbeId) => {
    setGrundeinstellungen(prev => {
      const isSelected = prev.rohlingfarben.some(f => f.id === farbeId);
      if (isSelected) {
        return {
          ...prev,
          rohlingfarben: prev.rohlingfarben.filter(f => f.id !== farbeId)
        };
      }

      const farbe = rohlingfarben.find(f => f.id === farbeId);
      if (!farbe) return prev;

      return {
        ...prev,
        rohlingfarben: [...prev.rohlingfarben, farbe]
      };
    });
  };

  const handleMasterSvgChange = (e) => {
    const file = e.target.files?.[0] || null;
    setMasterSvgFile(file);
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();

    if (editingRohlingId) {
      // Beim Bearbeiten: Aktualisiere Grundeinstellungen
      try {
        console.log('Updating Rohling with grundeinstellungen:', grundeinstellungen);
        console.log('beidseitig value:', grundeinstellungen.beidseitig, 'type:', typeof grundeinstellungen.beidseitig);

        const formData = new FormData();
        formData.append('name', grundeinstellungen.name);
        formData.append('breite', grundeinstellungen.breite);
        formData.append('hoehe', grundeinstellungen.hoehe);
        formData.append('beidseitig', String(grundeinstellungen.beidseitig));
        formData.append('frontSameAsBack', String(grundeinstellungen.frontSameAsBack));
        formData.append('gravurfarbe', grundeinstellungen.gravurfarbe);
        formData.append('rohlingfarben', JSON.stringify(grundeinstellungen.rohlingfarben || []));
        if (masterSvgFile) {
          formData.append('masterSvg', masterSvgFile);
        }

        console.log('FormData beidseitig:', formData.get('beidseitig'));

        const updated = await updateRohling(editingRohlingId, formData);
        if (masterSvgFile && updated?.masterSvg) {
          setGrundeinstellungen(prev => ({ ...prev, masterSvg: updated.masterSvg }));
        }
        setMasterSvgFile(null);
        await loadRohlinge();
        showMessage('success', 'Rohling aktualisiert');
      } catch (error) {
        showMessage('error', 'Fehler beim Aktualisieren der Grundeinstellungen');
        return;
      }
    } else {
      // Beim Erstellen: Rohling anlegen
      try {
        console.log('Creating Rohling with grundeinstellungen:', grundeinstellungen);
        console.log('beidseitig value:', grundeinstellungen.beidseitig, 'type:', typeof grundeinstellungen.beidseitig);

        const formData = new FormData();
        formData.append('name', grundeinstellungen.name);
        formData.append('breite', grundeinstellungen.breite);
        formData.append('hoehe', grundeinstellungen.hoehe);
        formData.append('beidseitig', String(grundeinstellungen.beidseitig));
        formData.append('frontSameAsBack', String(grundeinstellungen.frontSameAsBack));
        formData.append('gravurfarbe', grundeinstellungen.gravurfarbe);
        formData.append('rohlingfarben', JSON.stringify(grundeinstellungen.rohlingfarben || []));
        if (masterSvgFile) {
          formData.append('masterSvg', masterSvgFile);
        }

        console.log('FormData beidseitig:', formData.get('beidseitig'));

        await createRohling(formData);
        await loadRohlinge();
        resetForm();
        showMessage('success', 'Rohling gespeichert.');
      } catch (error) {
        showMessage('error', 'Fehler beim Erstellen des Rohlings');
        return;
      }
    }

    setCurrentStep(1);
  };

  const handleNeueFarbvariante = () => {
    const neueFarbvariante = {
      id: null, // Wird erst beim Speichern gesetzt
      farbe: '',
      farbeHex: '#cccccc',
      mode: 'image',
      frontImage: null,
      backImage: null,
      frontImagePreview: null,
      backImagePreview: null,
      useSameImageForBoth: grundeinstellungen.frontSameAsBack,
      frontScale: 1,
      backScale: 1,
      frontImageOffset: { x: 0, y: 0 },
      backImageOffset: { x: 0, y: 0 },
      frontRotation: 0,
      backRotation: 0,
      isCalibrating: { front: false, back: false },
      isNew: true // Marker für neue Farbvariante
    };

    setFarbvarianten(prev => [...prev, neueFarbvariante]);
    setAktiveFarbvarianteIndex(farbvarianten.length);
  };

  const handleFarbvarianteChange = (field, value) => {
    setFarbvarianten(prev => {
      const updated = [...prev];
      const current = { ...updated[aktiveFarbvarianteIndex] };
      const next = { ...current, [field]: value };

      if (field === 'mode' && value === 'color') {
        next.frontImage = null;
        next.backImage = null;
        next.frontImagePreview = null;
        next.backImagePreview = null;
        next.isCalibrating = { front: false, back: false };
      }

      updated[aktiveFarbvarianteIndex] = next;
      return updated;
    });
  };

  const handleFarbvarianteFileChange = (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const preview = event.target.result;

      setFarbvarianten(prev => {
        const updated = [...prev];
        const aktiv = { ...updated[aktiveFarbvarianteIndex], mode: 'image' };

        if (side === 'front') {
          aktiv.frontImage = file;
          aktiv.frontImagePreview = preview;
          aktiv.isCalibrating = { ...aktiv.isCalibrating, front: true };

          // Wenn "Vorderseite = Rückseite" aktiviert ist (global oder für diese Variante)
          if ((aktiv.useSameImageForBoth || grundeinstellungen.frontSameAsBack) && grundeinstellungen.beidseitig) {
            aktiv.backImage = file;
            aktiv.backImagePreview = preview;
            aktiv.isCalibrating = { ...aktiv.isCalibrating, back: true };
          }
        } else if (side === 'back') {
          aktiv.backImage = file;
          aktiv.backImagePreview = preview;
          aktiv.isCalibrating = { ...aktiv.isCalibrating, back: true };
        }

        updated[aktiveFarbvarianteIndex] = aktiv;
        return updated;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleScaleChange = (side, value) => {
    setFarbvarianten(prev => {
      const updated = [...prev];
      const aktiv = { ...updated[aktiveFarbvarianteIndex] };

      if (side === 'front') {
        aktiv.frontScale = parseFloat(value);
        if ((aktiv.useSameImageForBoth || grundeinstellungen.frontSameAsBack) && grundeinstellungen.beidseitig) {
          aktiv.backScale = parseFloat(value);
        }
      } else {
        aktiv.backScale = parseFloat(value);
      }

      updated[aktiveFarbvarianteIndex] = aktiv;
      return updated;
    });
  };

  const handleRotationChange = (side, value) => {
    setFarbvarianten(prev => {
      const updated = [...prev];
      const aktiv = { ...updated[aktiveFarbvarianteIndex] };

      if (side === 'front') {
        aktiv.frontRotation = parseFloat(value);
        if ((aktiv.useSameImageForBoth || grundeinstellungen.frontSameAsBack) && grundeinstellungen.beidseitig) {
          aktiv.backRotation = parseFloat(value);
        }
      } else {
        aktiv.backRotation = parseFloat(value);
      }

      updated[aktiveFarbvarianteIndex] = aktiv;
      return updated;
    });
  };

  const finishCalibration = (side) => {
    setFarbvarianten(prev => {
      const updated = [...prev];
      const aktiv = { ...updated[aktiveFarbvarianteIndex] };
      aktiv.isCalibrating = { ...aktiv.isCalibrating, [side]: false };

      // Wenn "Vorderseite = Rückseite" aktiviert und Vorderseite kalibriert
      if ((aktiv.useSameImageForBoth || grundeinstellungen.frontSameAsBack) && grundeinstellungen.beidseitig && side === 'front') {
        aktiv.isCalibrating = { ...aktiv.isCalibrating, back: false };
      }

      updated[aktiveFarbvarianteIndex] = aktiv;
      return updated;
    });

    showMessage('success', `Kalibrierung für ${side === 'front' ? 'Vorderseite' : 'Rückseite'} abgeschlossen.`);
  };

  const recalibrate = (side) => {
    setFarbvarianten(prev => {
      const updated = [...prev];
      const aktiv = { ...updated[aktiveFarbvarianteIndex] };
      aktiv.isCalibrating = { ...aktiv.isCalibrating, [side]: true };
      updated[aktiveFarbvarianteIndex] = aktiv;
      return updated;
    });
  };

  const drawImageWithOverlays = (side) => {
    const canvas = side === 'front' ? frontCanvasRef.current : backCanvasRef.current;
    if (!canvas || farbvarianten.length === 0 || aktiveFarbvarianteIndex >= farbvarianten.length) return;

    const aktiv = farbvarianten[aktiveFarbvarianteIndex];
    const imagePreview = side === 'front' ? aktiv.frontImagePreview : aktiv.backImagePreview;
    const scale = side === 'front' ? aktiv.frontScale : aktiv.backScale;

    if (!imagePreview) return;

    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const rotation = side === 'front' ? aktiv.frontRotation : aktiv.backRotation;
      const isCalibrating = aktiv.isCalibrating?.[side] || false;

      if (isCalibrating) {
        // KALIBRIERUNGSMODUS
        const mmToPixel = 10; // 1mm = 10 Pixel
        const calibrationWidth = grundeinstellungen.breite * mmToPixel;
        const calibrationHeight = grundeinstellungen.hoehe * mmToPixel;

        const diagonal = Math.sqrt(scaledWidth * scaledWidth + scaledHeight * scaledHeight);
        const padding = 50;
        canvas.width = Math.max(diagonal, calibrationWidth) + padding * 2;
        canvas.height = Math.max(diagonal, calibrationHeight) + padding * 2;

        const imageOffset = side === 'front' ? aktiv.frontImageOffset : aktiv.backImageOffset;
        const centerX = canvas.width / 2 + imageOffset.x;
        const centerY = canvas.height / 2 + imageOffset.y;

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();

        // Kalibrierungsrechteck zeichnen
        const rectX = (canvas.width - calibrationWidth) / 2;
        const rectY = (canvas.height - calibrationHeight) / 2;

        ctx.strokeStyle = '#2196f3';
        ctx.lineWidth = 3;
        ctx.setLineDash([10, 5]);
        ctx.strokeRect(rectX, rectY, calibrationWidth, calibrationHeight);

        ctx.fillStyle = '#2196f3';
        ctx.font = 'bold 16px Arial';
        ctx.setLineDash([]);
        const label = `${grundeinstellungen.breite}mm × ${grundeinstellungen.hoehe}mm (Zielgroesse - FEST)`;
        ctx.fillText(label, rectX + 5, rectY - 10);

        ctx.fillStyle = 'rgba(33, 150, 243, 0.15)';
        ctx.fillRect(rectX, rectY, calibrationWidth, calibrationHeight);

        ctx.fillStyle = '#2196f3';
        ctx.font = '14px Arial';
        ctx.fillText('Skalieren & Verschieben Sie das Bild (Ziehen zum Verschieben)', 10, 30);
      } else {
        // NORMALMODUS
        const diagonal = Math.sqrt(scaledWidth * scaledWidth + scaledHeight * scaledHeight);
        canvas.width = diagonal;
        canvas.height = diagonal;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();
      }
    };

    img.src = imagePreview;
  };

  const getMousePos = (canvas, evt) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (evt.clientX - rect.left) * scaleX,
      y: (evt.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (e, side) => {
    const canvas = side === 'front' ? frontCanvasRef.current : backCanvasRef.current;
    const pos = getMousePos(canvas, e);

    if (farbvarianten.length > 0 && aktiveFarbvarianteIndex < farbvarianten.length) {
      const aktiv = farbvarianten[aktiveFarbvarianteIndex];
      if (aktiv.isCalibrating?.[side]) {
        setIsDraggingImage(true);
        setDrawingSide(side);
        setDragStartOffset(pos);
      }
    }
  };

  const handleMouseMove = (e, side) => {
    const canvas = side === 'front' ? frontCanvasRef.current : backCanvasRef.current;
    const pos = getMousePos(canvas, e);

    if (isDraggingImage && drawingSide === side && farbvarianten.length > 0) {
      const aktiv = farbvarianten[aktiveFarbvarianteIndex];
      if (aktiv.isCalibrating?.[side]) {
        const currentOffset = side === 'front' ? aktiv.frontImageOffset : aktiv.backImageOffset;
        const dx = pos.x - dragStartOffset.x;
        const dy = pos.y - dragStartOffset.y;

        setFarbvarianten(prev => {
          const updated = [...prev];
          updated[aktiveFarbvarianteIndex] = {
            ...updated[aktiveFarbvarianteIndex],
            [`${side}ImageOffset`]: {
              x: currentOffset.x + dx,
              y: currentOffset.y + dy
            }
          };
          return updated;
        });

        setDragStartOffset(pos);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDraggingImage(false);
    setDrawingSide(null);
  };

  
const handleFarbvarianteSpeichern = async () => {
    if (farbvarianten.length === 0 || aktiveFarbvarianteIndex >= farbvarianten.length) {
      showMessage('error', 'Keine Farbvariante ausgew?hlt');
      return;
    }

    const aktiv = farbvarianten[aktiveFarbvarianteIndex];
    const mode = aktiv.mode || (aktiv.frontImage || aktiv.backImage ? 'image' : 'color');

    if (!aktiv.farbe || aktiv.farbe.trim() === '') {
      showMessage('error', 'Bitte geben Sie einen Farbnamen ein');
      return;
    }

    if (mode === 'image') {
      if (!aktiv.frontImage) {
        showMessage('error', 'Bitte laden Sie ein Vorderseitenbild hoch');
        return;
      }
    } else {
      const hexOk = aktiv.farbeHex && /^#[0-9A-Fa-f]{6}$/.test(aktiv.farbeHex);
      if (!hexOk) {
        showMessage('error', 'Bitte w?hlen Sie eine Farbe (Hex) aus');
        return;
      }
      if (!grundeinstellungen.masterSvg && !masterSvgFile) {
        showMessage('error', 'Bitte laden Sie zuerst ein Master-SVG hoch (Schritt 1)');
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('farbe', aktiv.farbe);
      formData.append('mode', mode);
      formData.append('farbeHex', aktiv.farbeHex || '');
      if (mode === 'color') {
        formData.append('clearImages', 'true');
      }
      formData.append('useSameImageForBoth', aktiv.useSameImageForBoth);
      formData.append('frontScale', aktiv.frontScale);
      formData.append('frontImageOffset', JSON.stringify(aktiv.frontImageOffset));
      formData.append('frontRotation', aktiv.frontRotation);

      if (grundeinstellungen.beidseitig) {
        if (aktiv.useSameImageForBoth) {
          formData.append('backScale', aktiv.frontScale);
          formData.append('backImageOffset', JSON.stringify(aktiv.frontImageOffset));
          formData.append('backRotation', aktiv.frontRotation);
        } else {
          formData.append('backScale', aktiv.backScale);
          formData.append('backImageOffset', JSON.stringify(aktiv.backImageOffset));
          formData.append('backRotation', aktiv.backRotation);
        }
      }

      if (mode === 'image' && aktiv.frontImage instanceof File) {
        formData.append('frontImage', aktiv.frontImage);
      }

      if (grundeinstellungen.beidseitig && mode === 'image') {
        if (aktiv.useSameImageForBoth && aktiv.frontImage instanceof File) {
          formData.append('backImage', aktiv.frontImage);
        } else if (aktiv.backImage instanceof File) {
          formData.append('backImage', aktiv.backImage);
        }
      }

      if (aktiv.isNew) {
        // Neue Farbvariante erstellen
        const neueFarbvariante = await addFarbvariante(editingRohlingId, formData);

        // Aktualisiere lokale Farbvariante mit Server-ID
        setFarbvarianten(prev => {
          const updated = [...prev];
          updated[aktiveFarbvarianteIndex] = {
            ...updated[aktiveFarbvarianteIndex],
            id: neueFarbvariante.id,
            isNew: false,
            mode: neueFarbvariante.mode || mode,
            farbeHex: neueFarbvariante.farbeHex || aktiv.farbeHex
          };
          return updated;
        });

        showMessage('success', 'Farbvariante erfolgreich gespeichert');
      } else {
        // Bestehende Farbvariante aktualisieren
        await updateFarbvariante(editingRohlingId, aktiv.id, formData);
        setFarbvarianten(prev => {
          const updated = [...prev];
          updated[aktiveFarbvarianteIndex] = {
            ...updated[aktiveFarbvarianteIndex],
            mode,
            farbeHex: aktiv.farbeHex
          };
          return updated;
        });
        showMessage('success', 'Farbvariante erfolgreich aktualisiert');
      }

    } catch (error) {
      showMessage('error', 'Fehler beim Speichern der Farbvariante');
    }
  };
const handleFarbvarianteLoeschen = async (index) => {
    if (farbvarianten.length <= 1) {
      if (window.confirm('Dies ist die letzte Farbvariante. Wenn Sie sie löschen, wird der gesamte Rohling entfernt. Fortfahren?')) {
        try {
          await deleteRohling(editingRohlingId);
          showMessage('success', 'Rohling erfolgreich gelöscht');
          loadRohlinge();
          resetForm();
        } catch (error) {
          showMessage('error', 'Fehler beim Löschen des Rohlings');
        }
      }
      return;
    }

    if (!window.confirm('Möchten Sie diese Farbvariante wirklich löschen?')) {
      return;
    }

    const farbvariante = farbvarianten[index];

    try {
      if (!farbvariante.isNew) {
        // Nur vom Server löschen wenn es bereits gespeichert wurde
        await deleteFarbvariante(editingRohlingId, farbvariante.id);
      }

      // Entferne aus lokalem State
      setFarbvarianten(prev => prev.filter((_, i) => i !== index));

      // Passe aktiven Index an
      if (aktiveFarbvarianteIndex >= farbvarianten.length - 1) {
        setAktiveFarbvarianteIndex(Math.max(0, farbvarianten.length - 2));
      }

      showMessage('success', 'Farbvariante erfolgreich gelöscht');
    } catch (error) {
      showMessage('error', 'Fehler beim Löschen der Farbvariante');
    }
  };

  const handleRohlingFertigstellen = async () => {
    if (farbvarianten.length === 0) {
      showMessage('error', 'Bitte fügen Sie mindestens eine Farbvariante hinzu');
      return;
    }

    // Prüfe ob alle Farbvarianten gespeichert wurden
    const hasUnsaved = farbvarianten.some(fv => fv.isNew);
    if (hasUnsaved) {
      showMessage('error', 'Bitte speichern Sie alle Farbvarianten bevor Sie fortfahren');
      return;
    }

    await loadRohlinge();
    resetForm();
    showMessage('success', 'Rohling erfolgreich erstellt/aktualisiert');
  };

  const handleEdit = async (rohling) => {
    setGrundeinstellungen({
      name: rohling.name,
      breite: rohling.breite || 30,
      hoehe: rohling.hoehe || 40,
      beidseitig: rohling.beidseitig || false,
      frontSameAsBack: rohling.frontSameAsBack || false,
      gravurfarbe: rohling.gravurfarbe || gravurfarben[0]?.hexColor || '',
      masterSvg: rohling.masterSvg || null,
      rohlingfarben: rohling.rohlingfarben || []
    });
    setMasterSvgFile(null);

    // Lade Farbvarianten
    const loadedFarbvarianten = (rohling.farbvarianten || []).map(fv => ({
      id: fv.id,
      farbe: fv.farbe,
      farbeHex: fv.farbeHex || '',
      mode: fv.mode || (fv.frontImage || fv.backImage ? 'image' : 'color'),
      frontImage: null,
      backImage: null,
      frontImagePreview: fv.frontImage ? buildAssetUrl(fv.frontImage) : null,
      backImagePreview: fv.backImage ? buildAssetUrl(fv.backImage) : null,
      useSameImageForBoth: fv.useSameImageForBoth || rohling.frontSameAsBack || false,
      frontScale: fv.frontScale || 1,
      backScale: fv.backScale || 1,
      frontImageOffset: fv.frontImageOffset || { x: 0, y: 0 },
      backImageOffset: fv.backImageOffset || { x: 0, y: 0 },
      frontRotation: fv.frontRotation || 0,
      backRotation: fv.backRotation || 0,
      isCalibrating: { front: false, back: false },
      isNew: false
    }));

    setFarbvarianten(loadedFarbvarianten);
    setAktiveFarbvarianteIndex(0);
    setEditingRohlingId(rohling.id);
    setCurrentStep(1); // Starte bei Grundeinstellungen, damit man diese sehen und bearbeiten kann
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diesen Rohling wirklich löschen?')) {
      try {
        await deleteRohling(id);
        showMessage('success', 'Rohling erfolgreich gelöscht');
        loadRohlinge();
      } catch (error) {
        showMessage('error', 'Fehler beim Löschen des Rohlings');
      }
    }
  };

  const handleToggleActive = async (rohling) => {
    const nextActive = rohling.active === false;
    const nextLabel = nextActive ? 'aktivieren' : 'inaktiv setzen';

    if (!window.confirm(`Möchten Sie "${rohling.name}" wirklich ${nextLabel}?`)) return;

    try {
      const formData = new FormData();
      formData.append('active', String(nextActive));

      const updated = await updateRohling(rohling.id, formData);
      setRohlinge(prev => prev.map(r => (r.id === rohling.id ? { ...r, active: updated.active } : r)));
      showMessage('success', `Rohling ${nextActive ? 'aktiviert' : 'inaktiv gesetzt'}`);
    } catch (error) {
      showMessage('error', 'Fehler beim Aktualisieren des Status');
    }
  };

  const handleNewRohling = () => {
    if (showForm) {
      resetForm();
    } else {
      resetForm();
      setShowForm(true);
    }
  };

  if (loading) {
    return <div className="loading">Lade Rohlinge...</div>;
  }

  const aktiveFarbvariante = farbvarianten[aktiveFarbvarianteIndex];

  return (
    <div className="rohling-verwaltung">
      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      <div className="header">
        <h2>Rohlinge</h2>
        <button className="btn btn-primary" onClick={handleNewRohling}>
          {showForm ? 'Abbrechen' : '+ Neuer Rohling'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <h3>{editingRohlingId ? 'Rohling bearbeiten' : 'Neuer Rohling'}</h3>

          <div className="steps-indicator">
            <div className="step active">
              <div className="step-number">1</div>
              <div>Grundeinstellungen</div>
            </div>
            {showFarbvarianten && (
              <div className={`step ${currentStep === 2 ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div>Farbvarianten</div>
              </div>
            )}
          </div>

          {currentStep === 1 && (
            <form onSubmit={handleStep1Submit} className="form-section">
              <h4>Allgemeine Eigenschaften</h4>
               <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input id="name" type="text" name="name" value={grundeinstellungen.name} onChange={handleGrundeinstellungenChange} placeholder="z.B. Schlüsselanhänger Rechteck" required />
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="breite">Breite (mm) *</label>
                        <input id="breite" type="number" name="breite" value={grundeinstellungen.breite}  onChange={handleGrundeinstellungenChange} step="0.1" min="1" required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="hoehe">Höhe (mm) *</label>
                        <input id="hoehe" type="number" name="hoehe" value={grundeinstellungen.hoehe} onChange={handleGrundeinstellungenChange} step="0.1" min="1" required />
                    </div>
                </div>
                <div className="form-group">
                  <label htmlFor="masterSvg">Master-SVG (leerer Rohling) *</label>
                  <input
                    id="masterSvg"
                    type="file"
                    accept=".svg"
                    onChange={handleMasterSvgChange}
                    required={!grundeinstellungen.masterSvg}
                  />
                  {grundeinstellungen.masterSvg && (
                    <p className="hint">Aktuelles SVG: {grundeinstellungen.masterSvg}</p>
                  )}
                </div>
                 <div className="form-group">
                  <label className="checkbox-label" htmlFor="beidseitig">
                    <input id="beidseitig" type="checkbox" name="beidseitig" checked={grundeinstellungen.beidseitig} onChange={handleGrundeinstellungenChange} />
                    <span>Beidseitig gravierbar</span>
                  </label>
                </div>
                {grundeinstellungen.beidseitig && (
                  <div className="form-group">
                    <label className="checkbox-label" htmlFor="frontSameAsBack">
                      <input id="frontSameAsBack" type="checkbox" name="frontSameAsBack" checked={grundeinstellungen.frontSameAsBack} onChange={handleGrundeinstellungenChange} />
                      <span>Vorderseite = Rückseite (gleiche Einstellungen und Bild für beide Seiten)</span>
                    </label>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="gravurfarbe">Gravurfarbe (für Text und Symbole) *</label>
                  <div className="gravurfarbe-select-wrapper">
                    <select id="gravurfarbe" name="gravurfarbe" value={grundeinstellungen.gravurfarbe} onChange={handleGrundeinstellungenChange} required>
                      {gravurfarben.length === 0 ? (
                        <option value="">Keine Gravurfarben verfügbar</option>
                      ) : (
                        gravurfarben.map(farbe => (
                          <option key={farbe.id} value={farbe.hexColor}>
                            {farbe.name} ({farbe.hexColor})
                          </option>
                        ))
                      )}
                    </select>
                    {grundeinstellungen.gravurfarbe && (
                      <div
                        className="color-preview"
                        style={{ backgroundColor: grundeinstellungen.gravurfarbe }}
                        title={grundeinstellungen.gravurfarbe}
                      />
                    )}
                  </div>
                </div>
              <div className="form-group">
                <label>Rohling-Farben (Palette)</label>
                {rohlingfarben.length === 0 ? (
                  <p className="hint">Keine Rohling-Farben angelegt.</p>
                ) : (
                  <div className="rohlingfarben-options">
                    {rohlingfarben.map(farbe => {
                      const checked = grundeinstellungen.rohlingfarben.some(sel => sel.id === farbe.id);
                      return (
                        <label key={farbe.id} className="checkbox-label rohlingfarbe-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleRohlingfarbeToggle(farbe.id)}
                          />
                          <span
                            className="color-preview small"
                            style={{ backgroundColor: farbe.hexColor }}
                            title={farbe.hexColor}
                          />
                          <span>{farbe.name} ({farbe.hexColor})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {showFarbvarianten ? 'Weiter zu Farbvarianten' : (editingRohlingId ? 'Änderungen speichern' : 'Rohling speichern')}
                </button>
              </div>
            </form>
          )}

          {showFarbvarianten && currentStep === 2 && (
             <div className="form-section">
                <h4>Farbvarianten</h4>
                
                <div className="farbvarianten-tabs">
                  {farbvarianten.map((fv, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`tab ${aktiveFarbvarianteIndex === index ? 'active' : ''}`}
                      onClick={() => setAktiveFarbvarianteIndex(index)}
                    >
                      {fv.farbe || `Variante ${index + 1}`}
                      {fv.isNew && ' *'}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="tab tab-add"
                    onClick={handleNeueFarbvariante}
                  >
                    + Neue Farbe
                  </button>
                </div>

                {aktiveFarbvariante && (
                  <div className="farbvariante-form">
                    <div className="form-group">
                      <label htmlFor="farbe">Farbname *</label>
                      <input
                        id="farbe"
                        type="text"
                        value={aktiveFarbvariante.farbe}
                        onChange={(e) => handleFarbvarianteChange('farbe', e.target.value)}
                        placeholder="z.B. Schwarz, Rot, Blau"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Darstellung</label>
                      <div className="mode-switch">
                        <label className="checkbox-label">
                          <input
                            type="radio"
                            name={`mode-${aktiveFarbvarianteIndex}`}
                            value="image"
                            checked={(aktiveFarbvariante.mode || 'image') === 'image'}
                            onChange={() => handleFarbvarianteChange('mode', 'image')}
                          />
                          <span>Foto hochladen</span>
                        </label>
                        <label className="checkbox-label" style={{ marginLeft: '12px' }}>
                          <input
                            type="radio"
                            name={`mode-${aktiveFarbvarianteIndex}`}
                            value="color"
                            checked={aktiveFarbvariante.mode === 'color'}
                            onChange={() => handleFarbvarianteChange('mode', 'color')}
                          />
                          <span>Farbe aus Palette</span>
                        </label>
                      </div>
                    </div>

                    {aktiveFarbvariante.mode === 'color' && (
                      <div className="form-group">
                        <label>Farbwert (Hex) *</label>
                        <div className="color-picker-group">
                          <input
                            type="color"
                            value={aktiveFarbvariante.farbeHex || '#cccccc'}
                            onChange={(e) => handleFarbvarianteChange('farbeHex', e.target.value)}
                            required
                          />
                          <input
                            type="text"
                            value={aktiveFarbvariante.farbeHex || ''}
                            onChange={(e) => handleFarbvarianteChange('farbeHex', e.target.value)}
                            placeholder="#cccccc"
                            pattern="^#[0-9A-Fa-f]{6}$"
                            title="Hex-Farbcode (z.B. #FF00AA)"
                            required
                          />
                          {grundeinstellungen.masterSvg && (
                            <span className="hint">Master-SVG wird mit dieser Farbe gef?llt</span>
                          )}
                        </div>
                      </div>
                    )}

                    {aktiveFarbvariante.mode !== 'color' && (
                    <div className="images-section">
                      <div className="image-upload-section">
                        <h5>Vorderseite</h5>
                        <div className="form-group">
                          <label htmlFor="frontImage">Bild hochladen</label>
                          <input
                            id="frontImage"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFarbvarianteFileChange(e, 'front')}
                          />
                        </div>
                        
                        {aktiveFarbvariante.frontImagePreview && (
                          <div className="calibration-area">
                            <canvas
                              ref={frontCanvasRef}
                              className="calibration-canvas"
                              onMouseDown={(e) => handleMouseDown(e, 'front')}
                              onMouseMove={(e) => handleMouseMove(e, 'front')}
                              onMouseUp={handleMouseUp}
                              onMouseLeave={handleMouseUp}
                            />
                            
                            {aktiveFarbvariante.isCalibrating?.front && (
                              <div className="calibration-controls">
                                <div className="form-group">
                                  <label>Skalierung: {aktiveFarbvariante.frontScale.toFixed(2)}</label>
                                  <input
                                    type="range"
                                    min="0.1"
                                    max="3"
                                    step="0.01"
                                    value={aktiveFarbvariante.frontScale}
                                    onChange={(e) => handleScaleChange('front', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Rotation: {aktiveFarbvariante.frontRotation}?</label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    step="1"
                                    value={aktiveFarbvariante.frontRotation}
                                    onChange={(e) => handleRotationChange('front', e.target.value)}
                                  />
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-success"
                                  onClick={() => finishCalibration('front')}
                                >
                                  Kalibrierung abschlie?en
                                </button>
                              </div>
                            )}
                            
                            {!aktiveFarbvariante.isCalibrating?.front && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => recalibrate('front')}
                              >
                                Neu kalibrieren
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {grundeinstellungen.beidseitig && !grundeinstellungen.frontSameAsBack && (
                        <div className="image-upload-section">
                          <h5>R?ckseite</h5>
                          <div className="form-group">
                            <label htmlFor="backImage">Bild hochladen</label>
                            <input
                              id="backImage"
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFarbvarianteFileChange(e, 'back')}
                            />
                          </div>
                          
                          {aktiveFarbvariante.backImagePreview && (
                            <div className="calibration-area">
                              <canvas
                                ref={backCanvasRef}
                                className="calibration-canvas"
                                onMouseDown={(e) => handleMouseDown(e, 'back')}
                                onMouseMove={(e) => handleMouseMove(e, 'back')}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                              />
                              
                              {aktiveFarbvariante.isCalibrating?.back && (
                                <div className="calibration-controls">
                                  <div className="form-group">
                                    <label>Skalierung: {aktiveFarbvariante.backScale.toFixed(2)}</label>
                                    <input
                                      type="range"
                                      min="0.1"
                                      max="3"
                                      step="0.01"
                                      value={aktiveFarbvariante.backScale}
                                      onChange={(e) => handleScaleChange('back', e.target.value)}
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Rotation: {aktiveFarbvariante.backRotation}?</label>
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      step="1"
                                      value={aktiveFarbvariante.backRotation}
                                      onChange={(e) => handleRotationChange('back', e.target.value)}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => finishCalibration('back')}
                                  >
                                    Kalibrierung abschlie?en
                                  </button>
                                </div>
                              )}
                              
                              {!aktiveFarbvariante.isCalibrating?.back && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => recalibrate('back')}
                                >
                                  Neu kalibrieren
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {grundeinstellungen.beidseitig && grundeinstellungen.frontSameAsBack && (
                        <div className="info-message">
                          <p>Hinweis: Das Vorderseitenbild wird automatisch auch f?r die R?ckseite verwendet.</p>
                        </div>
                      )}
                    </div>
                    )}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleFarbvarianteSpeichern}
                      >
                        {aktiveFarbvariante.isNew ? 'Farbvariante speichern' : 'Änderungen speichern'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleFarbvarianteLoeschen(aktiveFarbvarianteIndex)}
                      >
                        Farbvariante löschen
                      </button>
                    </div>
                  </div>
                )}

                {farbvarianten.length === 0 && (
                  <div className="empty-state">
                    <p>Keine Farbvarianten vorhanden. Klicken Sie auf "+ Neue Farbe", um eine hinzuzufügen.</p>
                  </div>
                )}

                <div className="form-actions final-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setCurrentStep(1)}
                  >
                    ← Zurück zu Grundeinstellungen
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleRohlingFertigstellen}
                  >
                    Rohling fertigstellen
                  </button>
                </div>
             </div>
          )}
        </div>
      )}

      <div className="rohlinge-grid">
        {rohlinge.length === 0 && !loading ? (
          <p className="no-data">Keine Rohlinge vorhanden.</p>
        ) : (
          rohlinge.map(rohling => (
            <div key={rohling.id} className={`card rohling-card ${rohling.active === false ? 'inactive' : ''}`}>
              <div className="rohling-card-header">
                <div className="rohling-card-title">
                  <h3>{rohling.name}</h3>
                  <span className={`rohling-status ${rohling.active === false ? 'inactive' : 'active'}`}>
                    {rohling.active === false ? 'Inaktiv' : 'Aktiv'}
                  </span>
                </div>
                <div className="rohling-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => handleToggleActive(rohling)}>
                      {rohling.active === false ? 'Aktivieren' : 'Inaktiv setzen'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(rohling)}>Bearbeiten</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(rohling.id)}>Löschen</button>
                </div>
              </div>
              <div className="rohling-card-body">
                {(rohling.masterSvg || (rohling.farbvarianten && rohling.farbvarianten.length > 0 && rohling.farbvarianten[0].frontImage)) && (
                  <div className="rohling-preview">
                    <img
                      src={buildAssetUrl(rohling.masterSvg || rohling.farbvarianten[0].frontImage)}
                      alt={`Vorschau ${rohling.name}`}
                      className="rohling-preview-image"
                    />
                  </div>
                )}
                <p><strong>Größe:</strong> {rohling.breite}mm × {rohling.hoehe}mm</p>
                <p><strong>Typ:</strong> {rohling.beidseitig ? 'Beidseitig' : 'Einseitig'}</p>
                <div className="farbvarianten-list">
                    <strong>Farben:</strong>
                    <div className="size-badges">
                        {(rohling.rohlingfarben && rohling.rohlingfarben.length > 0
                          ? rohling.rohlingfarben
                          : (rohling.farbvarianten || [])
                        ).map(fv => (
                            <span key={fv.id || fv.name} className="size-badge">{fv.name || fv.farbe}</span>
                        ))}
                    </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default RohlingVerwaltung;
